import { useChatAppearance } from '../core/client/useChatAppearance'
import { useSidebar } from '../context/SidebarContext'
import ChatSidebar from '../components/Sidebar/ChatSidebar'
import SidebarToggleButton from '../components/Sidebar/SidebarToggleButton'
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react'
import {
  IconArrowLeft as ArrowLeft,
  IconPlus as PlusIcon,
  IconSearch as Search,
  IconSparkles as Sparkles,
  IconStar as Star,
  IconX as X,
} from '@tabler/icons-react'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AgentCard from '../components/cards/AgentCard'
import CreateAgentDialog from '../components/CreateAgentDialog'
import { useUser } from '../hooks/auth/useUser'

import { useAgents } from '../hooks/useAgents'
import { useDepartments } from '../hooks/agents/useDepartments'
import { useLanguage } from '../hooks/utility/useLanguage'

import { ErrorState, LoadingState } from '../global/components'
import useMobile from '../hooks/utility/useMobile'
import { Agent, isInLibrary, JobCategory, MAIN_CHAT_AGENT_SLUG, Tag } from '../types/agentTypes'
import { getIconByName } from '../utils/iconUtils'

const LibraryPage: React.FC = () => {
  const { sidebarWidth } = useChatAppearance()
  const { isOpen: isSidebarOpen, toggle: toggleSidebar } = useSidebar()
  const navigate = useNavigate()
  const { t } = useLanguage()
  const isMobile = useMobile()
  const toast = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null)
  const [selectedJobCategory, setSelectedJobCategory] = useState<string | null>(null)
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [showCreateAgent, setShowCreateAgent] = useState(false)
  const { data: user } = useUser()

  // Color mode values
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const emptyStateIconColor = useColorModeValue('gray.400', 'gray.500')

  const { agents, isLoadingAgents, agentError, toggleAgentFavorite } = useAgents()
  const { departments } = useDepartments()

  const isReshapedAdmin = !!user?.is_reshaped_admin
  const libraryAgents = useMemo(
    () => (agents ?? []).filter(agent => isInLibrary(agent, isReshapedAdmin)),
    [agents, isReshapedAdmin],
  )

  // Calculate favorite count
  const favoriteCount = useMemo(() => libraryAgents.filter(agent => agent.is_preferred).length, [libraryAgents])

  // Get unique job categories for the selected department
  const jobCategoriesForDepartment = useMemo(() => {
    if (!selectedDepartment) return []

    const departmentAgents = libraryAgents.filter(agent => agent.department?.id === selectedDepartment)

    const jobCategoriesMap = new Map<string, JobCategory>()

    departmentAgents.forEach(agent => {
      if (agent.job_category) {
        jobCategoriesMap.set(agent.job_category.id, agent.job_category)
      }
    })

    return Array.from(jobCategoriesMap.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [libraryAgents, selectedDepartment])

  // Filter agents based on search query, department, job category, and favorites
  const filteredAgents = useMemo(() => {
    let filtered = [...libraryAgents]

    // Filter by favorites if selected
    if (showFavoritesOnly) {
      filtered = filtered.filter(agent => agent.is_preferred)
    }

    // Filter by department if selected
    if (selectedDepartment) {
      filtered = filtered.filter(agent => agent.department?.id === selectedDepartment)
    }

    // Filter by job category if selected
    if (selectedJobCategory) {
      filtered = filtered.filter(agent => agent.job_category?.id === selectedJobCategory)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        agent =>
          agent.name.toLowerCase().includes(query) ||
          (agent.description && agent.description.toLowerCase().includes(query)) ||
          agent.tags.some((tag: Tag) => tag.name.toLowerCase().includes(query)),
      )
    }

    // The main chat first, then alphabetically by name
    return filtered.sort(
      (a, b) =>
        Number(b.slug === MAIN_CHAT_AGENT_SLUG) - Number(a.slug === MAIN_CHAT_AGENT_SLUG) ||
        a.name.localeCompare(b.name),
    )
  }, [libraryAgents, searchQuery, selectedDepartment, selectedJobCategory, showFavoritesOnly])

  const handleAgentSelect = (agent: Agent) => {
    navigate(`/a/${agent.id}`)
    toast({
      title: t('library.agentSelected'),
      description: `${agent.name} ${t('library.isNowActive')}`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    })
  }

  const handleEditAgent = (agent: Agent) => {
    navigate(`/library/a/${agent.id}`)
  }

  const handleToggleFavorite = (agentId: string) => {
    toggleAgentFavorite(agentId)
  }

  const handleCreateAgent = () => {
    setShowCreateAgent(true)
  }

  const handleCreateAgentClose = () => {
    setShowCreateAgent(false)
  }

  // The active filter uses the sidebar's selected-row style and says so to screen readers.
  const filterTab = (active: boolean) => ({
    bg: active ? 'chat.selected' : 'transparent',
    color: active ? 'chat.brand' : 'chat.ink',
    'aria-pressed': active,
  })

  const handleBack = () => {
    navigate('/')
  }

  const clearSearchQuery = () => {
    setSearchQuery('')
  }

  const handleDepartmentSelect = (departmentId: string | null) => {
    setSelectedDepartment(departmentId)
    setSelectedJobCategory(null) // Clear job category when switching departments
    setShowFavoritesOnly(false)
  }

  const handleJobCategorySelect = (jobCategoryId: string | null) => {
    setSelectedJobCategory(jobCategoryId)
  }

  if (isLoadingAgents) {
    return <LoadingState message={t('common.loading')} useContainer variant='spinner' size='lg' />
  }

  if (agentError) {
    return <ErrorState message={agentError} useContainer onAction={() => window.location.reload()} />
  }

  return (
    <Box minH='100dvh' bg='chat.canvas' color='chat.ink'>
      <ChatSidebar
        onNewChat={handleBack}
        onSelectAgent={id => navigate(`/a/${id}`)}
        onSelectConversation={(id, agentId) => navigate(agentId ? `/a/${agentId}/c/${id}` : `/c/${id}`)}
        isSidebarOpenExternal={isSidebarOpen}
        onSidebarToggle={toggleSidebar}
      />
      <SidebarToggleButton isOpen={isSidebarOpen} onToggle={toggleSidebar} isFloating />
      <Box ml={isSidebarOpen && !isMobile ? sidebarWidth : 0} transition='margin-left 0.3s ease-in-out'>
        <Container maxW='1040px' px={{ base: 5, md: 10 }} pt={{ base: 20, md: 24 }} pb={12}>
          <VStack spacing={7} align='stretch'>
            {/* Store header shares the chat typography and navigation. */}
            <Flex justify='space-between' align='flex-start' gap={4} flexWrap='wrap'>
              <Box flex='1' minW='200px'>
                <Heading
                  fontSize={{ base: 'chat.title', md: '3xl' }}
                  size={isMobile ? 'lg' : 'xl'}
                  textAlign='left'
                  fontWeight='bold'
                  letterSpacing='-0.8px'
                  color='chat.ink'
                >
                  {t('library.library')}
                </Heading>
                <Text mt={2} color='chat.muted' fontSize='chat.body' lineHeight='1.6' maxW='580px'>
                  {t('library.subtitle')}
                </Text>
              </Box>
              {user?.is_superuser && (
                <Button
                  leftIcon={<PlusIcon size={18} strokeWidth={1.75} />}
                  onClick={handleCreateAgent}
                  variant='outline'
                  colorScheme='gray'
                  borderColor='chat.border'
                  borderRadius='chat.md'
                  size='md'
                  flexShrink={0}
                  fontSize='sm'
                >
                  {t('library.createAgent')}
                </Button>
              )}
            </Flex>

            {/* Search and Stats */}
            <Box>
              <InputGroup mb={3} size='lg'>
                <InputLeftElement pointerEvents='none' h='chat.search' color='chat.muted'>
                  <Search size={20} />
                </InputLeftElement>
                <Input
                  placeholder={t('library.searchPlaceholder')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  borderRadius='lg'
                  h='chat.search'
                  fontSize='chat.body'
                  borderColor='chat.border'
                  boxShadow='chat.input'
                  focusBorderColor='chat.focus'
                  _focusVisible={{ borderColor: 'chat.focus', boxShadow: 'chat.input' }}
                  bg='chat.canvas'
                />
                {searchQuery && (
                  <InputRightElement>
                    <IconButton
                      icon={<X size={16} />}
                      aria-label='Clear search'
                      size='sm'
                      variant='ghost'
                      onClick={clearSearchQuery}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <Flex justify='space-between' align='center' px={1}>
                <Text fontSize='sm' color={textMuted}>
                  {filteredAgents.length} {t('library.of')} {libraryAgents.length} {t('library.assistants')}
                </Text>
              </Flex>
            </Box>

            {/* Filter Tabs */}
            <Box
              sx={{
                '& button': { borderRadius: 'chat.md', border: 'none', fontWeight: 500, height: '38px', px: 4 },
              }}
            >
              <Flex gap={2} mb={0} flexWrap='wrap' align='flex-start'>
                {/* Show subcategories when department is selected, otherwise show main buttons */}
                {selectedDepartment && jobCategoriesForDepartment.length > 0 ? (
                  <>
                    {/* Back to department selection */}
                    <Button
                      size='sm'
                      variant='outline'
                      colorScheme='gray'
                      onClick={() => {
                        setSelectedDepartment(null)
                        setSelectedJobCategory(null)
                      }}
                      leftIcon={<ArrowLeft size={14} />}
                      iconSpacing={1}
                      px={3}
                    >
                      {t('common.back')}
                    </Button>

                    {/* Department name indicator with hierarchy styling */}
                    <Button
                      size='sm'
                      variant={!selectedJobCategory ? 'solid' : 'outline'}
                      colorScheme='gray'
                      {...filterTab(!selectedJobCategory)}
                      onClick={() => handleJobCategorySelect(null)}
                      px={3}
                      fontWeight='semibold'
                    >
                      {departments.find(d => d.id === selectedDepartment)?.name}
                    </Button>

                    {/* Hierarchy separator */}
                    <Text fontSize='sm' color='gray.400' alignSelf='center' px={1}>
                      →
                    </Text>

                    {/* Job category buttons */}
                    {jobCategoriesForDepartment.map(jobCategory => {
                      const jobIcon = getIconByName(jobCategory.icon_url || null)
                      return (
                        <Button
                          key={jobCategory.id}
                          size='sm'
                          variant={selectedJobCategory === jobCategory.id ? 'solid' : 'ghost'}
                          colorScheme='gray'
                          {...filterTab(selectedJobCategory === jobCategory.id)}
                          onClick={() => handleJobCategorySelect(jobCategory.id)}
                          leftIcon={jobIcon || undefined}
                          px={3}
                          iconSpacing={1}
                        >
                          {jobCategory.name}
                        </Button>
                      )
                    })}
                  </>
                ) : (
                  <>
                    {/* Main filter buttons */}
                    <Button
                      size='sm'
                      variant={showFavoritesOnly ? 'solid' : 'outline'}
                      colorScheme='gray'
                      {...filterTab(showFavoritesOnly)}
                      onClick={() => {
                        setShowFavoritesOnly(true)
                        setSelectedDepartment(null)
                        setSelectedJobCategory(null)
                      }}
                      leftIcon={<Star size={14} />}
                      iconSpacing={1}
                      px={3}
                    >
                      {t('library.favorites')} ({favoriteCount})
                    </Button>
                    <Button
                      size='sm'
                      variant={!selectedDepartment && !showFavoritesOnly ? 'solid' : 'outline'}
                      colorScheme='gray'
                      {...filterTab(!selectedDepartment && !showFavoritesOnly)}
                      onClick={() => handleDepartmentSelect(null)}
                      px={3}
                    >
                      {t('library.all')}
                    </Button>

                    {/* Department buttons */}
                    {departments.map(department => {
                      const icon = getIconByName(department.icon_url || null)
                      return (
                        <Button
                          key={department.id}
                          size='sm'
                          variant='outline'
                          colorScheme='gray'
                          onClick={() => handleDepartmentSelect(department.id)}
                          leftIcon={icon || undefined}
                          px={3}
                          iconSpacing={1}
                        >
                          {department.name}
                        </Button>
                      )
                    })}
                  </>
                )}
              </Flex>
            </Box>

            {/* Agents Grid */}
            {filteredAgents.length === 0 ? (
              <Flex justify='center' align='center' minH='300px'>
                <VStack spacing={4}>
                  <Sparkles size={48} color={emptyStateIconColor} />
                  <Text color={textMuted} fontSize='lg' textAlign='center'>
                    {searchQuery || selectedDepartment || selectedJobCategory || showFavoritesOnly
                      ? t('library.noSearchResults')
                      : t('library.noAgents')}
                  </Text>
                  {!searchQuery &&
                    !selectedDepartment &&
                    !selectedJobCategory &&
                    !showFavoritesOnly &&
                    user?.is_superuser && (
                      <Button
                        leftIcon={<PlusIcon size={16} />}
                        colorScheme='primaryScheme'
                        variant='outline'
                        onClick={handleCreateAgent}
                      >
                        {t('library.createFirstAgent')}
                      </Button>
                    )}
                </VStack>
              </Flex>
            ) : (
              <SimpleGrid columns={{ base: 1, lg: 2 }} columnGap={8} rowGap={2}>
                {filteredAgents.map(agent => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    isSelected={false}
                    isSuperuser={!!user?.is_superuser}
                    onSelect={handleAgentSelect}
                    onEdit={handleEditAgent}
                    onToggleFavorite={handleToggleFavorite}
                  />
                ))}
              </SimpleGrid>
            )}
          </VStack>
        </Container>
      </Box>

      {/* Create Agent Dialog */}
      <CreateAgentDialog isOpen={showCreateAgent} onClose={handleCreateAgentClose} />
    </Box>
  )
}

export default LibraryPage
